import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import api from '../../utils/authUtils';
import { useLanguage } from '../../contexts/LanguageContext';
import { getRandomTopicImage } from '../../utils/randomTopicImage';

const BLUE = '#47ABDB';

// Same headline treatment as the page's own hero (bold caps + italic
// subtitle, no background box) rather than a separate colored banner.
const SectionHeadline = styled.div`color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.04em;font-size:1.35rem;margin-bottom:.2rem;`;
const SectionSubtitle = styled.div`color:#5B5B5B;font-style:italic;font-size:.92rem;margin-bottom:1.1rem;`;

const Grid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem;@media(max-width:640px){grid-template-columns:1fr;}`;
const Card = styled.div`background:#fff;box-shadow:0 6px 16px rgba(15,23,42,.06);padding:1.1rem 1.2rem 1rem;position:relative;display:flex;flex-direction:column;overflow:hidden;`;
const NewRibbon = styled.div`
  position:absolute;top:0;right:0;background:linear-gradient(to right,${BLUE},#A0C9DA);color:#fff;
  font-size:.62rem;font-weight:800;letter-spacing:.03em;padding:.2rem 2.2rem;transform:rotate(45deg) translate(1.6rem,-.2rem);
`;
const TopRow = styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:.75rem;`;
const Tag = styled.span`display:inline-block;background:#003C72;color:#fff;font-size:.68rem;font-weight:600;padding:.2rem .55rem;align-self:flex-start;`;
const TopicImage = styled.img`width:56px;height:56px;object-fit:cover;border-radius:6px;flex:none;`;
const Title = styled.div`color:${BLUE};font-weight:800;font-size:1.18rem;line-height:1.32;margin-top:.65rem;`;
const Excerpt = styled.p`
  color:#0f2a3d;font-size:.82rem;line-height:1.5;margin:.5rem 0 0;flex:1;
  display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;
`;
const MatchNote = styled.div`color:#0f4c68;font-size:.72rem;font-style:italic;margin-top:.5rem;`;
const ReadButton = styled.a`
  display:flex;align-items:center;justify-content:center;margin-top:.85rem;padding:.6rem 1rem;
  color:#fff;font-weight:700;font-size:.85rem;text-decoration:none;
  background:linear-gradient(to right,${BLUE},#A0C9DA);transition:opacity .2s;
  &:hover{opacity:.9;}
`;

// A handful of real PubMed studies, fair-use-translated by the separate
// Heartport Expert project (expert.heartport.org) and loosely matched
// server-side to this account's topic interests - see
// StudyRecommendationService on the backend for how the matching works,
// and its fallback to general cardiology reads when nothing matches.
const RecommendedStudies = ({ t }) => {
  const { language } = useLanguage();
  const [studies, setStudies] = useState(null);

  useEffect(() => {
    api.get('/api/attendee/studies', { params: { lang: language } })
      .then(({ data }) => setStudies(data))
      .catch(() => setStudies([]));
  }, [language]);

  // Picked once per fetched list (not on every render) - same random-image-
  // per-card idea as Heartport Expert's own PubMedCard/ArticleCard, just not
  // re-rolled on every unrelated re-render.
  const imagesByPmid = useMemo(() => {
    const map = {};
    (studies || []).forEach((study) => { map[study.pmid] = getRandomTopicImage(); });
    return map;
  }, [studies]);

  if (!studies || studies.length === 0) return null;

  return (
    <div>
      {/* Brand name + tagline, hardcoded like the profile's own hero
          headline - not routed through react-intl. */}
      <SectionHeadline>HEARTPORT EXPERT</SectionHeadline>
      <SectionSubtitle>Latest Research for busy Doctors</SectionSubtitle>
      <Grid>
        {studies.map((study) => {
          const image = imagesByPmid[study.pmid];
          return (
            <Card key={study.pmid}>
              {study.isNew && <NewRibbon>{t('profile.studies.new', 'NEW')}</NewRibbon>}
              <TopRow>
                {study.journalName && <Tag>{study.journalName}</Tag>}
                {image && <TopicImage src={image} alt="" />}
              </TopRow>
              <Title>{study.title}</Title>
              {study.excerpt && <Excerpt>{study.excerpt}</Excerpt>}
              {study.matchedInterest && (
                <MatchNote>{t('profile.studies.matchedInterest', 'Because you’re interested in {interest}', { interest: t(`speaker.interest.${study.matchedInterest}`, study.matchedInterest) })}</MatchNote>
              )}
              <ReadButton href={study.url} target="_blank" rel="noreferrer">{t('profile.studies.readStudy', 'Read study')}</ReadButton>
            </Card>
          );
        })}
      </Grid>
    </div>
  );
};

export default RecommendedStudies;
